"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const helpers_1 = require("./helpers");
const useDerivedState_1 = __importDefault(require("../useDerivedState"));
/**
 * @typedef {object} InputNumber
 * @property {string} state
 * @property {() => void} handleOnDecrease
 * @property {() => void} handleOnIncrease
 * @property {(event: Event) => void} handleOnChange
 * @property {() => void} handleOnBlur
 */
/**
 * @param {object} options
 * @param {number | null} options.value
 * @param {(v: number | null) => void} options.onChange
 * @param {number | null} [options.precision]
 * @param {number | null} [options.max]
 * @param {number | null} [options.min]
 * @param {number} options.step
 * @param {boolean} [options.disableState]
 * @returns {InputNumber}
 */
const useInputNumber = ({ value, onChange, precision, max, min, step, disableState, }) => {
    const stateToValue = (0, react_1.useCallback)(
    /**
     * @param {string | null | undefined} x
     */
    (x) => (0, helpers_1.formatToNumber)(x, precision), [precision]);
    const valueToState = (0, react_1.useCallback)(
    /**
     * @param {string | number | null | undefined} x
     */
    (x) => (0, helpers_1.formatToString)(x, precision), [precision]);
    const [state, setState] = (0, useDerivedState_1.default)(value, onChange, stateToValue, valueToState, disableState);
    const handleOnDecrease = (0, react_1.useCallback)(() => {
        const newValue = (0, helpers_1.getSubtractedValue)({
            value,
            max,
            min,
            step,
            precision,
        });
        setState(valueToState(newValue));
    }, [value, max, min, step, precision]);
    const handleOnIncrease = (0, react_1.useCallback)(() => {
        const newValue = (0, helpers_1.getIncreasedValue)({
            value,
            max,
            min,
            step,
            precision,
        });
        setState(valueToState(newValue));
    }, [value, max, min, step, precision]);
    const handleOnChange = (0, react_1.useCallback)(
    /**
     * @param {Event} event
     */
    (event) => {
        if (event.target &&
            'value' in event.target &&
            typeof event.target.value === 'string') {
            setState(event.target.value);
        }
    }, [setState]);
    const handleOnBlur = (0, react_1.useCallback)(() => {
        // NOTE: This essentially mean: force cleaning state, i.e.
        //       apply precision constraints. Please note that it will
        //       not have any impact on the actual value because of:
        //       stateToValue * valueToState * stateToValue = stateToValue.
        setState(valueToState(stateToValue(state)));
    }, [state, setState]);
    return {
        state,
        handleOnDecrease,
        handleOnIncrease,
        handleOnChange,
        handleOnBlur,
    };
};
exports.default = useInputNumber;
//# sourceMappingURL=useInputNumber.js.map